import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { logoOptions } from '../components/LogoProposals';

/**
 * Logo Showcase Page
 * Displays all 10 logo proposals for One Table Mediation
 */
const LogoShowcase = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-navy-900 mb-4">
            One Table Mediation - Logo Proposals
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            10 professional logo concepts representing unity, collaboration, and resolution.
            Each design embodies the "One Table" philosophy of bringing parties together.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {logoOptions.map((option) => {
            const LogoComponent = option.component;
            return (
              <Card key={option.id} className="hover:shadow-xl transition-shadow">
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4 p-8 bg-white rounded-lg border-2 border-gray-100">
                    <LogoComponent className="h-20 w-20" />
                  </div>
                  <CardTitle className="text-navy-900">
                    Option {option.id}: {option.name}
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    {option.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Preview with white background */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center gap-3">
                      <LogoComponent className="h-8 w-8" />
                      <span className="font-semibold text-navy-900">One Table Mediation</span>
                    </div>
                    
                    {/* Preview with dark background */}
                    <div className="bg-navy-900 p-4 rounded-lg flex items-center gap-3">
                      <div className="text-white">
                        <LogoComponent className="h-8 w-8" />
                      </div>
                      <span className="font-semibold text-white">One Table Mediation</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recommended Options Highlight */}
        <Card className="bg-navy-50 border-navy-200">
          <CardHeader>
            <CardTitle className="text-2xl text-navy-900">
              ⭐ Top Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              {[4, 6, 8].map((optionId) => {
                const option = logoOptions.find(o => o.id === optionId);
                const LogoComponent = option.component;
                return (
                  <div key={optionId} className="bg-white p-6 rounded-lg text-center">
                    <div className="flex justify-center mb-4">
                      <LogoComponent className="h-16 w-16" />
                    </div>
                    <h3 className="font-bold text-navy-900 mb-2">{option.name}</h3>
                    <p className="text-sm text-gray-600">{option.description}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 p-4 bg-white rounded-lg">
              <h4 className="font-semibold text-navy-900 mb-2">Why these recommendations?</h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li><strong>Option 4 (Convergence):</strong> Visually represents multiple parties coming together at a common point - perfectly captures the mediation concept.</li>
                <li><strong>Option 6 (Common Ground):</strong> The overlapping circles symbolize finding the intersection where all parties agree.</li>
                <li><strong>Option 8 (One Table Minimalist):</strong> Direct, clean representation of the "One Table" name with modern minimalism.</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Usage Guidelines */}
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-navy-900">Logo Usage Guidelines</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-gray-700 space-y-2">
              <p><strong>Primary Color:</strong> Navy Blue (#1e3a5f)</p>
              <p><strong>Secondary Color:</strong> White (#ffffff)</p>
              <p><strong>Minimum Size:</strong> 32px height for digital, 1 inch for print</p>
              <p><strong>Clear Space:</strong> Maintain padding equal to logo height on all sides</p>
              <p><strong>Backgrounds:</strong> Works on white, light, and dark backgrounds</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-navy-900">Brand Personality</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-gray-700 space-y-2">
              <p><strong>Professional:</strong> Clean lines, balanced composition</p>
              <p><strong>Trustworthy:</strong> Stable, grounded design elements</p>
              <p><strong>Collaborative:</strong> Symbols of unity and connection</p>
              <p><strong>Modern:</strong> Contemporary style, timeless appeal</p>
              <p><strong>Neutral:</strong> Balanced, impartial visual language</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LogoShowcase;
